import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SelectField, TextField } from '@/components/form-fields';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth';
import {
  COUNTRY_OPTIONS,
  CURRENCY_OPTIONS,
  TIMEZONE_OPTIONS,
  US_STATE_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  withStoredValue,
} from '@/lib/store-options';
import {
  getStoreSettings,
  type StoreSettingsValues,
  saveStoreSettings,
  saveTradingName,
  type WeightUnit,
} from '@/lib/store-settings';

/**
 * Store details — the trading identity that appears on receipts, invoices and
 * transactional email, plus the units the whole console formats in.
 *
 * Ported from ctrl-saas's `settings/store` page. It reads and writes Supabase
 * directly, because nothing here needs a secret or service-role: RLS lets any
 * member read and only `settings.manage` write.
 *
 * The trading name is the exception and is handled separately below — it lives
 * on `public.accounts`, whose `accounts_self_update` policy admits the primary
 * owner alone.
 */

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const URL_LIKE = /^(https?:\/\/)?[^\s./]+(\.[^\s./]+)+(\/\S*)?$/i;
const PREFIX = /^[A-Za-z0-9-]*$/;

/**
 * The email and prefix patterns are copied from `43-store-settings.sql`
 * deliberately, so a value that passes here cannot fail the CHECK at write
 * time. Optional fields validate `'' or valid` rather than being `.optional()`:
 * the inputs are always controlled, so blank arrives as an empty string.
 */
const schema = z.object({
  legalName: z.string().trim().max(200, 'Keep this under 200 characters'),

  supportEmail: z
    .string()
    .trim()
    .max(200)
    .refine((value) => value === '' || EMAIL.test(value), 'Enter a valid email address'),

  supportPhone: z.string().trim().max(40, 'Keep this under 40 characters'),

  supportUrl: z
    .string()
    .trim()
    .max(500)
    .refine((value) => value === '' || URL_LIKE.test(value), 'Enter a valid web address'),

  websiteUrl: z
    .string()
    .trim()
    .max(500)
    .refine((value) => value === '' || URL_LIKE.test(value), 'Enter a valid web address'),

  addressLine1: z.string().trim().max(200),
  addressLine2: z.string().trim().max(200),
  city: z.string().trim().max(120),
  state: z.string().trim().max(60),
  zip: z.string().trim().max(20),
  country: z.string().trim().length(2),

  timezone: z.string().trim().min(1, 'Choose a timezone').max(60),
  currency: z.string().trim().length(3, 'Use a 3-letter currency code'),
  weightUnit: z.enum(['g', 'oz', 'lb', 'kg']),

  orderNumberPrefix: z
    .string()
    .trim()
    .max(8, 'Up to 8 characters')
    .refine((value) => PREFIX.test(value), 'Letters, numbers and hyphens only'),

  receiptFooter: z.string().trim().max(500, 'Keep this under 500 characters'),
});

type FormErrors = Partial<Record<keyof StoreSettingsValues | 'tradingName', string>>;

export function StoreSettings({ accountId }: { accountId: string }) {
  const { can, session } = useAuth();
  const userId = session?.user.id ?? '';
  const queryClient = useQueryClient();
  const canManage = can('settings.manage');

  const store = useQuery({
    queryKey: ['store-settings', accountId],
    queryFn: () => getStoreSettings(accountId, userId),
  });

  const [values, setValues] = useState<StoreSettingsValues | null>(null);
  const [tradingName, setTradingName] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Seed the form once the load lands, and re-seed after a save refetches.
  useEffect(() => {
    if (!store.data) return;

    setValues(store.data.values);
    setTradingName(store.data.tradingName);
    setErrors({});
  }, [store.data]);

  const save = useMutation({
    mutationFn: async (next: StoreSettingsValues) => {
      /*
       * Name first, deliberately. It is the write most likely to be refused
       * (primary owner only), so attempting it first means a refusal leaves
       * nothing written rather than half a save.
       */
      if (store.data && tradingName.trim() !== store.data.tradingName) {
        await saveTradingName(accountId, tradingName);
      }

      await saveStoreSettings(accountId, next);
    },
    onSuccess: () => {
      toast.success('Store details saved.');
      queryClient.invalidateQueries({ queryKey: ['store-settings', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (store.isPending || !values) {
    return (
      <div className="flex max-w-3xl flex-col gap-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (store.isError) {
    return (
      <p className="text-destructive text-sm">
        Could not load the store details: {(store.error as Error).message}
      </p>
    );
  }

  const data = store.data;

  function set<K extends keyof StoreSettingsValues>(key: K, value: StoreSettingsValues[K]) {
    setValues((current) => (current ? { ...current, [key]: value } : current));
    setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!values) return;

    const parsed = schema.safeParse(values);

    if (!parsed.success) {
      const next: FormErrors = {};

      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof StoreSettingsValues;
        next[field] ??= issue.message;
      }

      setErrors(next);
      toast.error('Some fields need attention.');
      return;
    }

    if (!tradingName.trim()) {
      setErrors({ tradingName: 'The workspace needs a name' });
      return;
    }

    save.mutate(parsed.data);
  }

  const isDirty =
    JSON.stringify(values) !== JSON.stringify(data.values) ||
    tradingName.trim() !== data.tradingName;

  // The curated lists must not drop a value an import wrote.
  const timezones = withStoredValue(TIMEZONE_OPTIONS, data.values.timezone);
  const currencies = withStoredValue(CURRENCY_OPTIONS, data.values.currency);
  const countries = withStoredValue(COUNTRY_OPTIONS, data.values.country);
  const states = withStoredValue(US_STATE_OPTIONS, data.values.state);

  return (
    /*
      `noValidate`: the browser's own constraint validation runs *before* the
      submit event, so `type="email"` on a malformed address blocked the submit
      entirely — the zod check never ran, and neither this field's error nor
      any other field's rendered. One validator, and it is the one whose
      messages match the rest of the form.
    */
    <form onSubmit={handleSubmit} noValidate className="flex max-w-3xl flex-col gap-6">
      {!canManage && (
        <p className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm">
          <Lock className="size-4 shrink-0" />
          You can see these details but not change them. That needs the
          &ldquo;settings.manage&rdquo; permission.
        </p>
      )}

      {/* One fieldset, so a read-only member gets every control disabled
          without each one repeating the check. */}
      <fieldset disabled={!canManage} className="flex flex-col gap-6">
        <SectionCard
          title="Store profile"
          description="How your business is named to customers and on paperwork."
        >
          <Field data-invalid={!!errors.tradingName}>
            <FieldLabel htmlFor="store-trading-name">Trading name</FieldLabel>

            {data.isPrimaryOwner ? (
              <>
                <Input
                  id="store-trading-name"
                  value={tradingName}
                  onChange={(event) => {
                    setTradingName(event.target.value);
                    setErrors((current) => ({ ...current, tradingName: undefined }));
                  }}
                  aria-invalid={!!errors.tradingName}
                />
                <FieldDescription>
                  The workspace name, shown to customers and across the console.
                </FieldDescription>
              </>
            ) : (
              <>
                {/* Read-only rather than hidden: it belongs on this screen, and
                    seeing it greyed out explains why it cannot be edited here. */}
                <div className="bg-muted/40 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                  <span className="min-w-0 truncate text-sm">{data.tradingName}</span>
                  <Lock className="text-muted-foreground size-3.5 shrink-0" />
                </div>
                <FieldDescription>
                  Only the workspace owner can change the trading name.
                </FieldDescription>
              </>
            )}

            {errors.tradingName && <FieldError>{errors.tradingName}</FieldError>}
          </Field>

          <TextField
            id="store-legal-name"
            label="Registered legal name"
            placeholder="Farmhouse Cannabis LLC"
            description="The entity on your licence and invoices, if it differs from the trading name."
            value={values.legalName}
            error={errors.legalName}
            onChange={(value) => set('legalName', value)}
          />
        </SectionCard>

        <SectionCard
          title="Contact"
          description="Shown on receipts and transactional email so customers can reach you."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="store-support-email"
              label="Support email"
              type="email"
              placeholder="hello@farmhouse.com"
              value={values.supportEmail}
              error={errors.supportEmail}
              onChange={(value) => set('supportEmail', value)}
            />

            <TextField
              id="store-support-phone"
              label="Support phone"
              type="tel"
              placeholder="(707) 555-0134"
              value={values.supportPhone}
              error={errors.supportPhone}
              onChange={(value) => set('supportPhone', value)}
            />
          </div>

          <TextField
            id="store-support-url"
            label="Help or contact page"
            placeholder="farmhouse.com/contact"
            description="We add https:// if you leave it off."
            value={values.supportUrl}
            error={errors.supportUrl}
            onChange={(value) => set('supportUrl', value)}
          />

          <TextField
            id="store-website-url"
            label="Your other website"
            placeholder="farmhouse.com"
            description="The site you had before, if you still have one. Kept for reference."
            value={values.websiteUrl}
            error={errors.websiteUrl}
            onChange={(value) => set('websiteUrl', value)}
          />
        </SectionCard>

        <SectionCard
          title="Business address"
          description="Your registered address. Storefront and delivery addresses are set per location."
        >
          <TextField
            id="store-address-1"
            label="Address"
            placeholder="1 Petaluma Blvd N"
            value={values.addressLine1}
            error={errors.addressLine1}
            onChange={(value) => set('addressLine1', value)}
          />

          <TextField
            id="store-address-2"
            label="Apartment, suite, etc."
            placeholder="Suite 200"
            value={values.addressLine2}
            error={errors.addressLine2}
            onChange={(value) => set('addressLine2', value)}
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              id="store-city"
              label="City"
              placeholder="Petaluma"
              value={values.city}
              error={errors.city}
              onChange={(value) => set('city', value)}
            />

            <SelectField
              id="store-state"
              label="State"
              options={states}
              placeholder="Select"
              value={values.state}
              onChange={(value) => set('state', value)}
            />

            <TextField
              id="store-zip"
              label="ZIP code"
              placeholder="94952"
              value={values.zip}
              error={errors.zip}
              onChange={(value) => set('zip', value)}
            />
          </div>

          <SelectField
            id="store-country"
            label="Country"
            options={countries}
            value={values.country}
            className="sm:max-w-xs"
            onChange={(value) => set('country', value)}
          />
        </SectionCard>

        <SectionCard
          title="Regional"
          description="The clock and units every report, cutoff and label is rendered in."
        >
          <SelectField
            id="store-timezone"
            label="Timezone"
            options={timezones}
            value={values.timezone}
            description="Reporting days, delivery windows and order cutoffs all use this zone."
            onChange={(value) => set('timezone', value)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="store-currency"
              label="Currency"
              options={currencies}
              value={values.currency}
              onChange={(value) => set('currency', value)}
            />

            <SelectField
              id="store-weight-unit"
              label="Weight unit"
              options={WEIGHT_UNIT_OPTIONS}
              value={values.weightUnit}
              description="Used for catalog weights and inventory counts."
              onChange={(value) => set('weightUnit', value as WeightUnit)}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Orders & receipts"
          description="How order numbers read and what prints at the end."
        >
          <TextField
            id="store-order-prefix"
            label="Order number prefix"
            placeholder="FH"
            description="Prepended to new order numbers, e.g. FH-1042. Up to 8 characters."
            className="sm:max-w-xs"
            value={values.orderNumberPrefix}
            error={errors.orderNumberPrefix}
            onChange={(value) => set('orderNumberPrefix', value)}
          />

          <Field data-invalid={!!errors.receiptFooter}>
            <FieldLabel htmlFor="store-receipt-footer">Receipt footer</FieldLabel>
            <Textarea
              id="store-receipt-footer"
              rows={3}
              placeholder="Thanks for shopping local. Keep cannabis away from children and pets."
              value={values.receiptFooter}
              onChange={(event) => set('receiptFooter', event.target.value)}
              aria-invalid={!!errors.receiptFooter}
            />
            <FieldDescription>
              Printed and emailed under every receipt. Up to 500 characters.
            </FieldDescription>
            {errors.receiptFooter && <FieldError>{errors.receiptFooter}</FieldError>}
          </Field>
        </SectionCard>
      </fieldset>

      {canManage && (
        /*
          Sticky inside the dialog's scroll container. The form is five sections
          long, so a Save pinned to the end of the document would be off-screen
          for most of the time anyone spends editing.
        */
        <div className="bg-background sticky bottom-0 -mx-8 flex items-center justify-end gap-3 border-t px-8 py-4">
          {isDirty && <span className="text-muted-foreground text-xs">Unsaved changes</span>}

          <Button type="submit" disabled={save.isPending || !isDirty}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
    </form>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-card text-card-foreground flex flex-col gap-4 rounded-lg border p-5">
      <div className="flex flex-col gap-y-0.5">
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>

      {children}
    </section>
  );
}
